// packages/shared-utils/src/main/mouseDeviceManager.ts
import { getSupportedDevicesSync } from "./supportedDevices";

type HidDevice = {
  vendorId: number;
  productId: number;
  productName: string;
  serialNumber?: string;
} & object;

type SupportedMouseDevice = {
  customerId: number;
  moudleId: number;
  type: number; // 0=dongle 1K, 1=dongle 4K, 2=wired 1K, 3=wired 8K, 4=dongle 2K, 5=dongle 8K
  deviceId: string;
  productName: string;
  vendorId: number;
  productId: number;
  sensor?: string;
  solutionRovider?: string;
};

interface MouseDeviceInstanceLike {
  getDeviceInfo: () => { info: { cid?: number; mid?: number; type?: number } };
}

interface MouseServiceLike {
  init: () => Promise<void>;
  connectDevice: (hidDevice: HidDevice) => Promise<string | null>;
  getDeviceInstance: (stableId: string) => MouseDeviceInstanceLike | null;
  disconnectDevice: (stableId: string) => Promise<void>;
}

let injectedMouseService: MouseServiceLike | null = null;

export function setMouseDeviceService(service: MouseServiceLike): void {
  injectedMouseService = service;
}

function getMouseDeviceService(): MouseServiceLike {
  if (!injectedMouseService) {
    throw new Error(
      "[MouseDeviceManager] mouseService 未注入，请先调用 setMouseDeviceService()"
    );
  }
  return injectedMouseService;
}

export interface MouseDeviceCache {
  vendorId: number;
  productId: number;
  cid: number;
  mid: number;
  type: number;
  deviceId: string;
  productName: string;
  lastUpdate: number;
  stableId?: string; // 新增字段：用于返回 MouseService 生成的唯一 ID
}

/**
 * 鼠标设备缓存管理器
 * 用于缓存通过 HID 协议获取的鼠标设备信息（CID/MID/Type）
 * 使用 WeakMap 按 HID 设备对象缓存，避免相同 VID/PID 的设备冲突
 */
class MouseDeviceManager {
  private cache = new WeakMap<HidDevice, MouseDeviceCache>();
  // 使用 cid-mid-type 作为持久化缓存键
  private persistentCache = new Map<string, MouseDeviceCache>();
  private cacheTimestamps = new Map<string, number>();
  // vendorId-productId → cid-mid-type 映射，用于反向查找
  private deviceKeyMap = new Map<string, string>();
  private readonly CACHE_DURATION = 30 * 60 * 1000; // 30分钟缓存有效期

  /**
   * 获取鼠标设备信息（带缓存）
   * @param hidDevice HID 设备对象
   * @param vendorId 供应商 ID
   * @param productId 产品 ID
   * @param forceRefresh 是否强制刷新（默认 false，优先读缓存）
   * @returns 设备信息或 null
   */
  async getDeviceInfo(
    hidDevice: HidDevice,
    vendorId: number,
    productId: number,
    forceRefresh = false
  ): Promise<MouseDeviceCache | null> {
    const mouseService = getMouseDeviceService();
    // 优先读取缓存（除非强制刷新）
    if (!forceRefresh) {
      const cached = this.getCachedDeviceInfo(hidDevice);
      if (cached) {
        return cached;
      }
    }

    // 确保 mouseService 已初始化
    await mouseService.init();

    return await this.processSingleDeviceRequest(
      hidDevice,
      vendorId,
      productId
    );
  }

  /**
   * 处理单个设备的 HID 协议请求
   * 新架构: 使用 mouseService.connectDevice 创建独立实例
   */
  private async processSingleDeviceRequest(
    hidDevice: HidDevice,
    vendorId: number,
    productId: number
  ): Promise<MouseDeviceCache | null> {
    const mouseService = getMouseDeviceService();
    if (!hidDevice) {
      console.error("[MouseDeviceManager] hidDevice 为 null");
      return null;
    }

    // 新架构: 通过 mouseService 创建独立设备实例
    let stableId: string | null = null;
    try {
      // 连接设备，创建独立实例
      stableId = await mouseService.connectDevice(hidDevice);

      if (!stableId) {
        console.error("[MouseDeviceManager] 设备连接失败");
        return null;
      }

      // 获取设备实例
      const device = mouseService.getDeviceInstance(stableId);
      if (!device) {
        console.error("[MouseDeviceManager] 无法获取设备实例");
        return null;
      }

      // 等待设备初始化完成
      await new Promise((resolve) => setTimeout(resolve, 300));

      // 获取设备信息
      const deviceInfo = device.getDeviceInfo();
      const info = deviceInfo.info;

      // 通过 HID 协议获取 CID/MID/Type
      let matched: SupportedMouseDevice | undefined;
      let deviceCid: number | undefined;
      let deviceMid: number | undefined;
      let deviceType: number | undefined;

      try {
        // 通过 CID/MID/Type 匹配设备
        if (
          typeof info?.cid === "number" &&
          typeof info?.mid === "number" &&
          typeof info?.type === "number"
        ) {
          const supportedMouseDevices = getSupportedDevicesSync()
            .mouse as unknown as SupportedMouseDevice[];

          // 优先匹配 cid + mid + type
          matched = supportedMouseDevices.find(
            (m) =>
              m.customerId === info.cid &&
              m.moudleId === info.mid &&
              m.type === info.type
          );

          // 如果没有精确匹配,则只匹配 cid + mid (兼容旧数据)
          if (!matched) {
            matched = supportedMouseDevices.find(
              (m) => m.customerId === info.cid && m.moudleId === info.mid
            );
          }

          if (matched) {
            deviceCid = info.cid;
            deviceMid = info.mid;
            deviceType = info.type;
          }
        }
      } catch (error) {
        // 设备信息获取失败
        console.error("[MouseDeviceManager] 获取设备CID/MID失败:", error);
        // 断开设备
        await mouseService.disconnectDevice(stableId);
        return null;
      }

      // 如果匹配失败，断开设备
      if (!matched) {
        await mouseService.disconnectDevice(stableId);
        return null;
      }

      const deviceCache: MouseDeviceCache = {
        vendorId,
        productId,
        cid: deviceCid ?? -1,
        mid: deviceMid ?? -1,
        type: typeof deviceType === "number" ? deviceType : -1,
        deviceId: matched?.deviceId || "unknownMouse",
        productName: matched?.productName || "Unknown Mouse",
        lastUpdate: Date.now(),
        stableId, // 返回 connectDevice 生成的 stableId
      };

      // 使用 cid-mid-type 作为缓存键
      const cacheKey = `${deviceCid}-${deviceMid}-${deviceType}`;
      const deviceKey = `${vendorId}-${productId}`;

      this.cache.set(hidDevice, deviceCache);
      this.persistentCache.set(cacheKey, deviceCache);
      this.cacheTimestamps.set(cacheKey, Date.now());
      this.deviceKeyMap.set(deviceKey, cacheKey);

      return deviceCache;
    } catch (error) {
      console.error("[MouseDeviceManager] 获取设备信息失败:", error);
      return null;
    } finally {
      // 仅用于探测 CID/MID/Type：默认探测后断开，避免实例残留
      if (stableId) {
        try {
          await mouseService.disconnectDevice(stableId);
        } catch (disconnectError) {
          console.warn(
            `[MouseDeviceManager] 探测后断开设备失败: ${stableId}`,
            disconnectError
          );
        }
      }
    }
  }

  /**
   * 获取缓存的设备信息（仅缓存，不请求 HID）
   */
  getCachedDeviceInfo(hidDevice: HidDevice): MouseDeviceCache | null {
    if (!hidDevice) return null;

    // 优先从 WeakMap 获取
    const weakCached = this.cache.get(hidDevice);
    if (weakCached) {
      const cacheKey = `${weakCached.cid}-${weakCached.mid}-${weakCached.type}`;
      const cachedTime = this.cacheTimestamps.get(cacheKey);
      if (cachedTime && Date.now() - cachedTime < this.CACHE_DURATION) {
        return weakCached;
      }
    }

    // 严禁使用 persistentCache 基于 VID/PID 进行模糊匹配
    // 在多设备场景下（如两个相同的匠盟接收器），它们的 VID/PID 完全相同。
    // 如果复用 persistentCache 中的数据，会导致第二个设备错误地继承第一个设备的 stableId，
    // 进而导致 UI 层去重逻辑误杀第二个设备。
    // 必须强制让新对象走 processSingleDeviceRequest -> connectDevice 流程，以生成唯一的后缀 ID。

    return null;
  }

  /**
   * 清除所有缓存
   */
  clearCache(): void {
    this.persistentCache.clear();
    this.cacheTimestamps.clear();
    this.deviceKeyMap.clear();
    // WeakMap 无法手动 clear，但清除其他缓存即可让缓存失效
  }

  /**
   * 移除指定设备的缓存
   */
  removeFromCache(hidDevice: HidDevice): void {
    if (!hidDevice) return;

    // 通过映射表获取 cacheKey
    const deviceKey = `${hidDevice.vendorId}-${hidDevice.productId}`;
    const cacheKey = this.deviceKeyMap.get(deviceKey);

    this.cache.delete(hidDevice);
    if (cacheKey) {
      this.cacheTimestamps.delete(cacheKey);
      this.persistentCache.delete(cacheKey);
    }
    this.deviceKeyMap.delete(deviceKey);
  }

  /**
   * 获取缓存统计信息
   */
  getCacheStats(): { size: number } {
    return {
      size: this.cacheTimestamps.size,
    };
  }
}

// 导出单例
export const mouseDeviceManager = new MouseDeviceManager();
